import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

export const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** A refresh's answer arrived for a session that is no longer the stored one. */
export class SessionChangedError extends Error {
  constructor() {
    super('The session changed while refreshing');
    this.name = 'SessionChangedError';
  }
}

/**
 * Whether an administrator is impersonating somebody: `AuthContext` keeps their
 * own tokens aside as `real_*`, and the stored refresh token is still theirs —
 * the impersonated session has none.
 */
function impersonating(): boolean {
  return localStorage.getItem('real_access_token') !== null;
}

/**
 * Each access token a refresh renewed, to the token that replaced it — the one
 * chain that is provably the same session. Kept in `localStorage` beside the
 * tokens, so a request waiting in another tab of the same browser sees a
 * renewal this tab made. A sign-in, a sign-out or an impersonation never
 * enters it, so a token from before one of those leads nowhere.
 */
const RENEWED_KEY = 'renewed_tokens';
const RENEWED_KEPT = 20;

/** This page's own renewals, kept even when storage refuses the shared record. */
const renewedHere: Array<[string, string]> = [];

function readRenewed(): Array<[string, string]> {
  try {
    const pairs: unknown = JSON.parse(localStorage.getItem(RENEWED_KEY) ?? '[]');
    return Array.isArray(pairs)
      ? pairs.filter(
          (p): p is [string, string] =>
            Array.isArray(p) && typeof p[0] === 'string' && typeof p[1] === 'string',
        )
      : [];
  } catch {
    return [];
  }
}

function recordRenewal(from: string, to: string) {
  renewedHere.push([from, to]);
  if (renewedHere.length > RENEWED_KEPT) renewedHere.shift();
  const pairs = [...readRenewed().filter(([old]) => old !== from), [from, to] as [string, string]];
  try {
    localStorage.setItem(RENEWED_KEY, JSON.stringify(pairs.slice(-RENEWED_KEPT)));
  } catch {
    // Storage refused: this page still knows (`renewedHere`); another tab does not.
  }
}

/**
 * Forgets every renewal: called whenever the session changes other than by a
 * refresh (sign-in, sign-out, impersonation started or stopped), so no token of
 * an ended session can lead to the new one.
 */
export function clearRenewals() {
  renewedHere.length = 0;
  try {
    localStorage.removeItem(RENEWED_KEY);
  } catch {
    // Nothing stored to forget.
  }
}

/** Whether `token` was renewed, by refreshes in this browser, into `current`. */
function renewedInto(token: string, current: string): boolean {
  const renewedTo = new Map([...renewedHere, ...readRenewed()]);
  let next = renewedTo.get(token);
  for (let hops = 0; next && hops < RENEWED_KEPT; hops++) {
    if (next === current) return true;
    next = renewedTo.get(next);
  }
  return false;
}

let refreshing: { refreshToken: string; promise: Promise<string> } | null = null;

/**
 * Runs `fn` while holding a lock every window of this browser shares, so two
 * windows never trade the same refresh token at once. Without the Web Locks
 * API it simply runs: the server keeps a traded token usable for a short grace
 * period, so a race costs a second token rather than the session.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? (locks.request('peles-auth-refresh', () => fn()) as Promise<T>) : fn();
}

/**
 * Another window refreshed the session `refreshToken` belonged to: answers the
 * access token it stored, provided it is provably a renewal of the one this
 * page held (`renewedInto`), else null — a sign-in, a sign-out or an
 * impersonation since is not this session.
 */
function renewedElsewhere(refreshToken: string, accessToken: string | null): string | null {
  const storedRefresh = localStorage.getItem('refresh_token');
  const current = localStorage.getItem('access_token');
  if (!storedRefresh || storedRefresh === refreshToken || impersonating()) return null;
  if (!accessToken || !current) return null;
  return renewedInto(accessToken, current) ? current : null;
}

/**
 * Trades the stored refresh token for a new pair and stores it, answering the
 * new access token. One refresh at a time per refresh token, shared by every
 * caller of that session — the refresh token rotates, so two refreshes racing
 * would spend it twice. Across windows the refresh runs under a shared lock
 * (`withRefreshLock`), and a window that waited behind another's refresh — or
 * whose own refresh lost the race — takes the pair that window stored instead
 * of failing (`renewedElsewhere`). A failure only rejects: it neither clears
 * the stored tokens nor leaves the page, so a caller that is not the person's
 * own request (the browser's crash logging) cannot sign them out by trying.
 *
 * Never while impersonating: the stored refresh token is the administrator's,
 * and its answer would swap the impersonated session for theirs. The answer is
 * stored only while the session that asked is still the one stored — a
 * sign-out, a new sign-in, or an impersonation started while the refresh was
 * on the wire must not be undone by it (`SessionChangedError`).
 */
export function refreshAccessToken(): Promise<string> {
  if (impersonating()) return Promise.reject(new Error('No refresh while impersonating'));
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.reject(new Error('No refresh token'));
  if (refreshing?.refreshToken === refreshToken) return refreshing.promise;
  const accessToken = localStorage.getItem('access_token');
  const promise: Promise<string> = withRefreshLock(async () => {
    // Waited behind another window's refresh: its pair is already stored.
    if (localStorage.getItem('refresh_token') !== refreshToken) {
      const renewed = renewedElsewhere(refreshToken, accessToken);
      if (renewed) return renewed;
      throw new SessionChangedError();
    }
    let data: { accessToken: string; refreshToken: string };
    try {
      ({ data } = await axios.post('/api/v1/auth/refresh', { refreshToken }));
    } catch (err) {
      // Another window spent the same token first and stored its answer.
      const renewed = renewedElsewhere(refreshToken, accessToken);
      if (renewed) return renewed;
      throw err;
    }
    if (localStorage.getItem('refresh_token') !== refreshToken || impersonating()) {
      // An impersonation began meanwhile and set this session aside: the old
      // refresh token is spent, so the set-aside pair takes the new one, or
      // "Stop impersonating" would restore a revoked token.
      if (localStorage.getItem('real_refresh_token') === refreshToken) {
        localStorage.setItem('real_access_token', data.accessToken);
        localStorage.setItem('real_refresh_token', data.refreshToken);
      }
      // Another window refreshed the same session meanwhile and stored its
      // pair first: that pair is as good as this one, so take it.
      const renewed = renewedElsewhere(refreshToken, accessToken);
      if (renewed) return renewed;
      throw new SessionChangedError();
    }
    if (accessToken) recordRenewal(accessToken, data.accessToken);
    localStorage.setItem('access_token', data.accessToken);
    localStorage.setItem('refresh_token', data.refreshToken);
    return data.accessToken;
  }).finally(() => {
    if (refreshing?.promise === promise) refreshing = null;
  });
  refreshing = { refreshToken, promise };
  return promise;
}

/**
 * Whether the server refused the session itself. Only that signs anybody out:
 * a refresh that could not reach the server, or met a 5xx, says nothing about
 * whether the sign-in is still good, and is tried again on the next request.
 */
export function sessionRefused(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403;
}

/**
 * A new impersonation token for the person being impersonated, signed with who
 * they are now — the one minted when the impersonation began carries the role
 * they had then, and is never refreshed. Asked with the administrator's own
 * session (set aside as `real_*`), renewed first if that token has expired.
 *
 * The answer is stored only while the same impersonation is still the stored
 * session (`SessionChangedError` otherwise), and recorded as a renewal so a
 * request still on the wire with the old token is sent again with this one.
 * One renewal at a time per impersonation token, shared by every caller (two
 * at once would spend the administrator's refresh token twice).
 */
let renewingImpersonation: { token: string; promise: Promise<string> } | null = null;

export function renewImpersonationToken(userId: string): Promise<string> {
  const impersonationToken = localStorage.getItem('access_token');
  if (renewingImpersonation?.token === impersonationToken) return renewingImpersonation.promise;
  const promise = renewImpersonation(userId).finally(() => {
    if (renewingImpersonation?.promise === promise) renewingImpersonation = null;
  });
  if (impersonationToken) renewingImpersonation = { token: impersonationToken, promise };
  return promise;
}

async function renewImpersonation(userId: string): Promise<string> {
  const impersonationToken = localStorage.getItem('access_token');
  let realToken = localStorage.getItem('real_access_token');
  if (!impersonationToken || !realToken) throw new Error('Not impersonating');
  const ask = (token: string) =>
    axios.post<{ accessToken: string }>(
      `/api/v1/auth/impersonate/${encodeURIComponent(userId)}`,
      null,
      { headers: { Authorization: `Bearer ${token}` } },
    );

  let answer;
  try {
    answer = await ask(realToken);
  } catch (err) {
    const realRefreshToken = localStorage.getItem('real_refresh_token');
    if (!axios.isAxiosError(err) || err.response?.status !== 401 || !realRefreshToken) throw err;
    const { data } = await axios.post('/api/v1/auth/refresh', { refreshToken: realRefreshToken });
    // The administrator's refresh token is spent now: keep the new pair even if
    // the impersonation ended meanwhile, so "Stop impersonating" restores a live one.
    if (localStorage.getItem('real_refresh_token') === realRefreshToken) {
      localStorage.setItem('real_access_token', data.accessToken);
      localStorage.setItem('real_refresh_token', data.refreshToken);
    } else if (!impersonating() && localStorage.getItem('refresh_token') === realRefreshToken) {
      // "Stop impersonating" put the administrator's pair back meanwhile. While
      // an impersonation lasts the stored refresh token is theirs too, so only
      // its end makes the main slot theirs to write.
      localStorage.setItem('access_token', data.accessToken);
      localStorage.setItem('refresh_token', data.refreshToken);
      throw new SessionChangedError();
    } else {
      throw new SessionChangedError();
    }
    realToken = data.accessToken as string;
    answer = await ask(realToken);
  }

  if (localStorage.getItem('access_token') !== impersonationToken || !impersonating()) {
    throw new SessionChangedError();
  }
  const renewed = answer.data.accessToken;
  recordRenewal(impersonationToken, renewed);
  localStorage.setItem('access_token', renewed);
  return renewed;
}

function signOut() {
  clearRenewals();
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/login';
}

/**
 * The impersonation token was refused (it expired): the administrator's own
 * session is put back, as "Stop impersonating" would, and the page reloads on
 * it — never refreshed into their session behind an impersonated screen.
 */
function endImpersonation() {
  clearRenewals();
  const realAccessToken = localStorage.getItem('real_access_token');
  const realRefreshToken = localStorage.getItem('real_refresh_token');
  if (realAccessToken) localStorage.setItem('access_token', realAccessToken);
  if (realRefreshToken) localStorage.setItem('refresh_token', realRefreshToken);
  localStorage.removeItem('real_access_token');
  localStorage.removeItem('real_refresh_token');
  window.location.href = '/';
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Refused with a token that is no longer the stored one. A token this page
    // renewed into the stored one (another request or the crash logging
    // refreshed it meanwhile) is the same session: the request is sent again
    // with the new token. Anything else — a sign-in, a sign-out or an
    // impersonation since it was sent — is only rejected: replaying it would
    // run an ended session's change under the session now stored.
    const current = localStorage.getItem('access_token');
    const sentWith = String(originalRequest.headers?.Authorization ?? '').replace(/^Bearer /, '');
    if (current && sentWith && sentWith !== current) {
      if (!renewedInto(sentWith, current)) return Promise.reject(error);
      originalRequest._retry = true;
      originalRequest.headers.Authorization = `Bearer ${current}`;
      return apiClient(originalRequest);
    }

    if (impersonating()) {
      endImpersonation();
      return Promise.reject(error);
    }

    if (!localStorage.getItem('refresh_token')) {
      signOut();
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    try {
      const token = await refreshAccessToken();
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // A refresh overtaken by another session (signed out, signed in again,
      // impersonating) says nothing about the session now stored: leave it.
      // A temporary failure (offline, a 5xx) keeps the tokens for the next try.
      if (!(refreshError instanceof SessionChangedError) && sessionRefused(refreshError)) signOut();
      return Promise.reject(refreshError);
    }
  },
);
